import { ref, watch, Ref, computed } from 'vue';
import { AutocompleteSuggestionEnhanced } from '@/infrastructure/AutocompleteSuggestionEnhanced';
import { PerformDebounce } from '@/platform/PerformDebounce';
import { ControlEvent } from '@/models/filter-tracking/ControlEvent';
import { FilterKey } from '@/models/FilterKey';
import { LocationType } from '../location-v2/LocationTypeEnum';

export class DropdownFilter {
	public inputRef = ref<HTMLInputElement | null>(null);
	public readonly input = ref('');
	public readonly suggestions = ref<readonly AutocompleteSuggestionEnhanced[]>([]);
	public readonly refOfTrackingProxy: Ref<Element | null> = ref(null);
	public readonly filterKey: FilterKey;
	public readonly selectionId = ref('');

	private readonly suggest: (query: string) => Promise<readonly AutocompleteSuggestionEnhanced[]>;
	private readonly processSelection: (value: string, key: FilterKey) => Promise<void>;
	private readonly inputDisplay: Ref<string>;
	private readonly debounceInput: ReturnType<PerformDebounce['for']>;
	private lastValidSelectionOption: Ref<string>;

	public constructor(
		suggest: (query: string) => Promise<readonly AutocompleteSuggestionEnhanced[]>,
		processSelection: (value: string, key: FilterKey) => Promise<void>,
		filterKey: FilterKey,
		lastValidSelectionOption: Ref<string>,
		inputDisplay: Ref<string>
	) {
		this.suggest = suggest;
		this.processSelection = processSelection;
		this.lastValidSelectionOption = lastValidSelectionOption;
		this.inputDisplay = inputDisplay;
		this.filterKey = filterKey;

		this.debounceInput = new PerformDebounce().for(() => void this.onInputChanged(this.input.value), 500);
	}

	public suggestionsByType = computed(() => {
		return this.suggestions.value.reduce<Record<string, AutocompleteSuggestionEnhanced[]>>((acc, item) => {
			const type = this.capitalizeFirstLetter(LocationType[item.type].toLowerCase());
			if (!acc[type]) {
				acc[type] = [];
			}
			acc[type].push(item);
			return acc;
		}, {});
	});

	public onMounted(): void {
		watch(this.inputDisplay, (value) => this.updateInput(value));
	}

	public onInput(event: Event): void {
		const target = event.target as HTMLInputElement;
		this.input.value = target.value;

		this.debounceInput();
	}

	public onBlur(): void {
		if (this.lastValidSelectionOption.value !== '' && this.suggestions.value.length === 0) {
			this.input.value = this.lastValidSelectionOption.value;
		}
	}

	public onClearInputClick(): void {
		this.updateInputForDisplay('');
		this.inputRef.value?.focus();
	}

	private async onInputChanged(query: string): Promise<void> {
		this.suggestions.value = await this.suggest(query);
	}

	public onSuggestionChosen(suggestion: AutocompleteSuggestionEnhanced): void {
		this.selectionId.value = suggestion.id.toString();

		if (this.refOfTrackingProxy.value) {
			this.refOfTrackingProxy.value.dispatchEvent(new Event(ControlEvent.CHANGE));
		}

		this.updateInputForDisplay('');

		void this.processSelection(suggestion.id.toString(), suggestion.key);
	}

	private updateInput(value: string): void {
		this.updateInputForDisplay(value);
		this.lastValidSelectionOption.value = value;
	}

	private updateInputForDisplay(value: string): void {
		this.input.value = value;
		this.suggestions.value = [];
	}

	private capitalizeFirstLetter(str: string): string {
		return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
	}
}
